import React, { useState, useRef } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import {
  InputElementFragment,
  FormInfoFragment,
  PublicFormElementInput,
  FormPrefillEntryFragment,
  ExpirySettingsPreviewFragment,
} from "__generated__/graphqlUnauthenticated";
import { PreviewFormItem } from "./PreviewFormItem";
import { Form, Formik } from "formik";
import { mapToInitialValues } from "./helpers/mapToInitialValues";
import { ConfirmationPage } from "./ConfirmationPage";
import { Loading } from "common/state/Loading";
import ReCAPTCHA from "react-google-recaptcha";
import { generateValidationSchema } from "./generateValidationSchema";
import { CoverPhotoPreview } from "../builder/main/coverPhoto/CoverPhotoPreview";
import { useLocation } from "react-router-dom";
import { ExpiryMessagePage } from "./ExpiryMessagePage";
import { useTranslation } from "react-i18next";
import { FormErrorMessage } from "./FormErrorMessage";
import { SupportedByFooter } from "./SupportedByFooter";
import { useFormFooter } from "./useFormFooter";

// hiding these for v1.0 since they require CRM lookup
export const hideTypes = [
  "EMPLOYEE",
  "MULTI_COMPANY",
  "MULTI_CONTACT",
  "MULTI_EMPLOYEE",
  "CURRENCY",
];
interface IPreviewFormUIProps {
  loading: boolean;
  formDetails: FormInfoFragment | null;
  prefill: FormPrefillEntryFragment[];
  onSubmit: (values: any) => Promise<{ error?: string }>;
  isDraft: boolean;
  errorMessage: string | undefined;
  setRecaptchaToken: (token: string) => void;
  loadingFormInfo: boolean;
  isExpired: boolean;
  expirySettings: ExpirySettingsPreviewFragment | null | undefined;
  ff_update_form_validation?: boolean;
}

export const PreviewFormUI: React.FC<IPreviewFormUIProps> = ({
  onSubmit,
  formDetails,
  isDraft,
  setRecaptchaToken,
  errorMessage,
  loading,
  prefill,
  loadingFormInfo,
  isExpired,
  expirySettings,
  ff_update_form_validation,
}) => {
  const { t: translate } = useTranslation("lists");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const captchaRef = useRef<any>(null);

  const footer = useFormFooter(formDetails?.defaultFooter);

  const handleOnSubmit = (values: any) => {
    const inputs: PublicFormElementInput[] = [];
    for (const key in values) {
      inputs.push({ elementId: key, value: values[key] });
    }
    onSubmit(inputs).then((result) => {
      if (!result.error) {
        // send message to parent window so clients can listen to the event and build custom logic
        window.parent.postMessage({ type: "formSubmitted" }, "*");
        setIsSubmitted(true);
      }
      captchaRef?.current?.reset();
    });
  };
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  if (!formDetails || loadingFormInfo)
    return (
      <Box height={"100vh"}>
        <Loading copy={translate("forms.preview.loading")} />
      </Box>
    );
  if (isExpired) {
    return (
      <ExpiryMessagePage
        header={expirySettings?.pageHeader || "Form Has Expired"}
        message={expirySettings?.pageDescription?.htmlForEditor}
      />
    );
  }
  if (isSubmitted)
    return (
      <ConfirmationPage
        confirmationPageSettings={formDetails.confirmationPageSettings}
      />
    );
  // Don't render form if waitlist redirect is required
  if (formDetails.waitlistSettings?.redirectRequired) {
    return (
      <Box height={"100vh"}>
        <Loading copy={translate("forms.preview.redirecting")} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        overflow: "scroll",
        display: "flex",
        justifyContent: "center",
        background: (theme) => theme.palette.background.lightBlue,
      }}
      data-cy="preview-form"
      className="nexl-form-wrapper" //required to customize the form theme from form general settings in the topbar admin
    >
      <Box
        sx={{
          width: { xs: "100%", md: 680 },
          p: 4,
          backgroundColor: (theme) => ` ${theme.palette.common.white}`,
        }}
      >
        {formDetails.theme.coverImage?.url && (
          <Box sx={{ mb: 2 }}>
            <CoverPhotoPreview imageUrl={formDetails.theme.coverImage.url} />
          </Box>
        )}
        <Typography variant="h4">{formDetails.title}</Typography>
        <Box>
          <Box my={2}>
            <Typography>{formDetails.description}</Typography>
          </Box>
          <Box my={2}>
            <Formik
              initialValues={mapToInitialValues(
                (formDetails.elements || []) as InputElementFragment[],
                prefill,
                urlParams,
              )}
              validationSchema={generateValidationSchema(
                (formDetails.elements || []) as InputElementFragment[],
                ff_update_form_validation,
              )}
              onSubmit={handleOnSubmit}
            >
              {({ isValid, submitCount }) => {
                return (
                  <Form>
                    {formDetails.elements.map((element: any, idx) => {
                      if (
                        (element.displayAs &&
                          hideTypes.includes(element.displayAs)) ||
                        element.hidden
                      )
                        return null;
                      return (
                        <React.Fragment key={element.id || idx}>
                          {element.__typename === "InputElement" ? (
                            <PreviewFormItem
                              formElement={element as InputElementFragment}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              dangerouslySetInnerHTML={{
                                __html: element.value,
                              }}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                    <Typography
                      component="div"
                      dangerouslySetInnerHTML={{ __html: footer }}
                    />
                    <Box mt={1} />
                    {!formDetails.disableRecaptcha && (
                      <ReCAPTCHA
                        sitekey="6Lf3VsYnAAAAALzPx9DbJd9q91p0errhZGXT5jYX"
                        onChange={setRecaptchaToken}
                        ref={captchaRef}
                      />
                    )}
                    <Box>
                      <FormErrorMessage
                        isValid={isValid}
                        submitCount={submitCount}
                        errorMessage={errorMessage}
                        ff_update_form_validation={ff_update_form_validation}
                      />
                    </Box>
                    <Button
                      size="medium"
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={isDraft || loading}
                      endIcon={loading && <CircularProgress size="16px" />}
                      sx={{ mt: 1 }}
                    >
                      {translate("forms.preview.submit")}
                    </Button>
                  </Form>
                );
              }}
            </Formik>
          </Box>
          <SupportedByFooter />
        </Box>
      </Box>
    </Box>
  );
};
